import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Phone, Sparkles, X } from "lucide-react";

const DISMISS_KEY = "stickyCTADismissed";

export default function StickyCTA() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined"
      ? sessionStorage.getItem(DISMISS_KEY) === "1"
      : false
  );

  useEffect(() => {
    if (dismissed) return;
    const onScroll = () => setVisible(window.scrollY > 500);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [dismissed]);

  const close = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  if (dismissed) return null;

  const whatsappLink =
    "https://wa.me/421907649964?text=Dobr%C3%BD%20de%C5%88%2C%20m%C3%A1m%20z%C3%A1ujem%20o%20cenov%C3%BA%20ponuku...";

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none px-4 pb-4 lg:pb-6"
          data-testid="sticky-cta"
        >
          <div className="pointer-events-auto max-w-3xl mx-auto">
            {/* Glow backdrop */}
            <div className="absolute inset-x-6 -top-6 h-12 bg-[#00E5FF]/15 blur-2xl rounded-full" />

            <div className="relative glass border border-white/10 rounded-2xl lg:rounded-full px-3 py-3 lg:px-4 lg:py-3 flex items-center gap-2 lg:gap-3 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
              {/* Label (desktop only) */}
              <div className="hidden lg:flex items-center gap-2 pl-3 pr-1">
                <Sparkles size={16} className="text-[#00E5FF]" />
                <span className="text-sm font-bold whitespace-nowrap">
                  Chcete ponuku?
                </span>
              </div>

              <div className="h-8 w-px bg-white/10 hidden lg:block" />

              {/* Actions */}
              <div className="flex-1 grid grid-cols-3 gap-2 lg:flex lg:items-center lg:gap-2">
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sticky-whatsapp"
                  className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BD5A] text-white font-bold text-sm py-3 px-3 lg:px-5 rounded-xl lg:rounded-full transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  <MessageCircle size={16} />
                  <span>WhatsApp</span>
                </a>

                <a
                  href="tel:+421907649964"
                  data-testid="sticky-phone"
                  className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold text-sm py-3 px-3 lg:px-5 rounded-xl lg:rounded-full transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Phone size={16} className="text-[#00E5FF]" />
                  <span className="hidden sm:inline">Telefón</span>
                  <span className="sm:hidden">Volať</span>
                </a>

                <a
                  href="#kontakt"
                  data-testid="sticky-form"
                  className="flex items-center justify-center gap-2 bg-[#00E5FF] hover:bg-[#33EEFF] text-black font-bold text-sm py-3 px-3 lg:px-5 rounded-xl lg:rounded-full transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Sparkles size={16} />
                  <span>Formulár</span>
                </a>
              </div>

              {/* Close */}
              <button
                onClick={close}
                className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/10 transition-colors"
                data-testid="sticky-close"
                aria-label="Zavrieť lištu"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
